/**
 * Volunteer Impact — the 9 benefits of volunteering, each as a
 * long-form article. The style, tone and structure mirror the
 * user-provided "Improve Wellbeing" reference article.
 *
 * Each entry:
 *   slug     — URL key, must be unique
 *   title    — short "brand" name (matches the card on /volunteer)
 *   tagline  — one-liner shown on the benefit card and article header
 *   headline — article-level H1 (usually a stat / promise)
 *   color    — accent hex matching the card icon
 *   emoji    — one-glyph icon
 *   hero     — Unsplash/Pexels hero image (1600w-optimised)
 *   intro    — 2-3 paragraph article intro
 *   sectionTitle — sub-heading for the list of benefits
 *   sections — [{ icon, title, body }, …]  (5-6 items)
 *   closing  — final "small acts, big changes" style paragraph
 */

export const VOLUNTEER_BENEFITS = [
  {
    slug: "help-people",
    title: "Help People",
    tagline: "Serve neighbours, families and communities that need your time.",
    headline: "Volunteering Puts Care Directly Where It Is Needed",
    color: "#e11d48",
    emoji: "❤️",
    hero: "https://images.unsplash.com/photo-1593113646773-028c64a8f1b8?w=1600&auto=format&fit=crop&q=80",
    intro: [
      "The simplest and most powerful reason to volunteer is that it helps people. A hot meal, a ride to a clinic, a listening ear, or a repaired roof can change the trajectory of someone's week — sometimes their year.",
      "When you volunteer, you become a first responder to everyday problems that governments and markets often miss. Whether the need is around the corner or on the other side of the world, you help create a safety net woven from ordinary kindness."
    ],
    sectionTitle: "How Volunteering Directly Helps People",
    sections: [
      { icon: "🍲", title: "Meets Immediate Needs", body: "Food, shelter, transport, medicine and clothing reach the people who need them most — quickly, and without red tape." },
      { icon: "🏥", title: "Fills the Gaps in Public Services", body: "Overstretched hospitals, schools and shelters rely on volunteers to keep essential programs running." },
      { icon: "🤗", title: "Restores Dignity", body: "Personal attention and respect matter as much as the tangible help. A friendly volunteer reminds people they are seen and valued." },
      { icon: "🌍", title: "Amplifies Local Knowledge", body: "You know your neighbourhood best. Local volunteers can respond faster and more appropriately than outside agencies." },
      { icon: "🕊️", title: "Prevents Crises", body: "Timely help — a warm coat before winter, a tutor before a child falls behind — prevents small problems from becoming big ones." },
    ],
    closing: "You do not need money, credentials, or a lot of time to help someone today. A single act, offered with care, is often the beginning of a much larger story of change.",
    testimonial: {
      quote: "The first time I dropped off groceries for an elderly neighbour, I realised how invisible she'd been to everyone else on our street. Two years later, half the block checks on her every week.",
      name: "Marta Ruiz",
      role: "Volunteer · Barcelona",
      avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200&auto=format&fit=crop&q=80",
    },
  },

  {
    slug: "build-relationships",
    title: "Build Relationships",
    tagline: "Meet mentors, teammates and lifelong friends.",
    headline: "Volunteering Creates Bonds That Outlast the Task",
    color: "#7c3aed",
    emoji: "🤝",
    hero: "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=1600&auto=format&fit=crop&q=80",
    intro: [
      "Some of the deepest, most durable friendships you will ever make will start over shared work. Sorting food on a Saturday, planting trees at dawn, tutoring a child every week — these moments introduce you to people you would never meet inside your usual circles.",
      "Volunteering builds relationships across generations, backgrounds and beliefs. It reminds us that we belong to something wider than our own family, workplace or scroll feed.",
    ],
    sectionTitle: "How Volunteering Grows Your Circle",
    sections: [
      { icon: "🌐", title: "Bridges Communities", body: "Meet people from different professions, cultures and generations that you would rarely encounter otherwise." },
      { icon: "🧑‍🏫", title: "Introduces You to Mentors", body: "Experienced volunteers, coordinators and community leaders are often quietly eager to guide the next generation." },
      { icon: "💞", title: "Builds Trust Quickly", body: "Shared purpose creates trust faster than small talk. Volunteering is an accelerated path to real friendship." },
      { icon: "🎉", title: "Creates Memories You Keep", body: "Community meals, cleanup days and shared victories become the stories you retell for years." },
      { icon: "📇", title: "Widens Your Support Network", body: "In moments when you need help — advice, references, a warm meal — your volunteer network shows up." },
    ],
    closing: "You come to give your time and leave with friends. That is the quiet gift volunteering keeps handing back, long after the shift is over.",
    testimonial: {
      quote: "I joined a Saturday tree-planting group to burn off screen time. Six months in, my closest friend is a 63-year-old retired teacher I never would have met otherwise. That's the real dividend of volunteering.",
      name: "Ade Okafor",
      role: "Community volunteer · Lagos",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&auto=format&fit=crop&q=80",
    },
  },

  {
    slug: "strengthen-communities",
    title: "Strengthen Communities",
    tagline: "Every hour of service makes your neighborhood safer and kinder.",
    headline: "Volunteering Builds the Social Fabric Around You",
    color: "#059669",
    emoji: "🌱",
    hero: "https://images.unsplash.com/photo-1544027993-37dbfe43562a?w=1600&auto=format&fit=crop&q=80",
    intro: [
      "Strong communities are not accidents. They are built quietly, one act of neighbourliness at a time — a fixed sidewalk, a shared garden, a mentor for a lonely teen, a warm meal for a struggling family.",
      "When enough people volunteer, ordinary neighbourhoods turn into places where children are safer, elders are cared for, and newcomers feel welcome. Social capital — the trust and goodwill between people — is the invisible infrastructure that makes life livable.",
    ],
    sectionTitle: "How Volunteering Strengthens Communities",
    sections: [
      { icon: "🏘️", title: "Increases Safety", body: "Volunteer patrols, neighbourhood watch groups and youth mentors reduce crime and isolation." },
      { icon: "🌳", title: "Improves Public Spaces", body: "Cleanups, tree planting and mural projects turn neglected corners into places of pride." },
      { icon: "🧓", title: "Cares For Elders and Kids", body: "Volunteers deliver meals, run afterschool programs, and keep families connected across generations." },
      { icon: "🚨", title: "Prepares For Emergencies", body: "Trained community volunteers respond fastest during floods, fires and power outages." },
      { icon: "🤲", title: "Welcomes Newcomers", body: "New neighbours, refugees and first-time parents integrate faster when a community welcomes them warmly." },
    ],
    closing: "A community is only as strong as the number of neighbours willing to show up for one another. Your hours become part of that strength.",
    testimonial: {
      quote: "Our neighbourhood watch started with three people and a WhatsApp group. Two winters later, we've run food drives, snow-shovelled for elders, and know every family on the street by name. Nothing feels safer than that.",
      name: "Hannah Björk",
      role: "Neighbourhood organiser · Stockholm",
      avatar: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=200&auto=format&fit=crop&q=80",
    },
  },

  {
    slug: "learn-new-skills",
    title: "Learn New Skills",
    tagline: "Gain training, certifications and hands-on experience.",
    headline: "Volunteering is a School That Never Sends a Bill",
    color: "#0ea5e9",
    emoji: "🎓",
    hero: "https://images.unsplash.com/photo-1509062522246-3755977927d7?w=1600&auto=format&fit=crop&q=80",
    intro: [
      "Volunteering is one of the fastest ways to learn skills you would otherwise pay for. Project management, public speaking, budgeting, first aid, coding, teaching, carpentry — all can be picked up while genuinely helping others.",
      "Because you learn by doing, the skills stick. And because the environment is supportive rather than competitive, you can try, fail, and grow without the pressure of a formal job.",
    ],
    sectionTitle: "Skills You Can Build While Volunteering",
    sections: [
      { icon: "🗣️", title: "Communication and Public Speaking", body: "Lead a briefing, moderate a workshop, or explain a program to a first-timer." },
      { icon: "📋", title: "Project and Event Management", body: "Plan a food drive, coordinate a cleanup, run a fundraiser end-to-end." },
      { icon: "💻", title: "Digital and Technical Skills", body: "Help nonprofits with websites, data, social media or basic IT support." },
      { icon: "🩺", title: "First Aid, Safety and Emergency Response", body: "Formal certifications are often provided free to active volunteers." },
      { icon: "🌍", title: "Language and Cultural Fluency", body: "Translation programs and cross-cultural work sharpen a rare and valuable skill." },
    ],
    closing: "Every volunteer role is also a curriculum. Show up curious, and you'll walk away with abilities that shape your career and your life.",
    testimonial: {
      quote: "I volunteered as an event-day coordinator because I wanted to practise leading meetings. A year later I run a 40-person team at work. The volunteer nights were my leadership school — and they cost nothing but time.",
      name: "Priya Menon",
      role: "Volunteer coordinator · Bengaluru",
      avatar: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200&auto=format&fit=crop&q=80",
    },
  },

  {
    slug: "gain-experience",
    title: "Gain Experience",
    tagline: "Real-world work that strengthens your résumé and college apps.",
    headline: "Volunteering Turns Time Into Résumé-Ready Experience",
    color: "#b45309",
    emoji: "💼",
    hero: "https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1600&auto=format&fit=crop&q=80",
    intro: [
      "Employers, admissions officers and mentors consistently rank volunteer experience alongside internships and jobs. It shows initiative, follow-through, and the ability to work with people beyond your own circle.",
      "Volunteering also lets you test-drive careers before committing. Curious about medicine, teaching, engineering, journalism or law? There is a volunteer role that puts you next to professionals doing exactly that work — every week.",
    ],
    sectionTitle: "How Volunteering Builds Career Capital",
    sections: [
      { icon: "📄", title: "Strengthens Your Résumé", body: "Sustained volunteer work signals reliability, empathy and leadership — traits employers actively seek." },
      { icon: "🏥", title: "Lets You Sample Careers", body: "Try healthcare, education, environmental work or advocacy before you commit to a degree or job." },
      { icon: "🧑‍💼", title: "Grows Your Professional Network", body: "Nonprofit boards and organizers are often generously connected across industries." },
      { icon: "📚", title: "Builds Verified Hours", body: "Many programs issue hour logs and reference letters recognised by schools and employers." },
      { icon: "🚀", title: "Opens Career Doors", body: "First jobs and paid roles frequently emerge from volunteer relationships and demonstrated impact." },
    ],
    closing: "The best résumés tell a story of someone who showed up when it mattered. Volunteering writes exactly that story — one shift at a time.",
    testimonial: {
      quote: "My first internship offer came from a doctor I met while volunteering at a free clinic. Nothing on my CV impressed her more than the sentence 'shows up every Sunday for the past year.' Consistency is the résumé.",
      name: "Daniel Rivera",
      role: "Pre-med student · Chicago",
      avatar: "https://images.unsplash.com/photo-1568602471122-7832951cc4c5?w=200&auto=format&fit=crop&q=80",
    },
  },

  {
    slug: "earn-recognition",
    title: "Earn Recognition",
    tagline: "Digital badges, community awards and verified hours.",
    headline: "Every Volunteer Hour Deserves to be Seen and Celebrated",
    color: "#d97706",
    emoji: "⭐",
    hero: "https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1600&auto=format&fit=crop&q=80",
    intro: [
      "Recognition is not the reason we volunteer, but it matters. Being seen, thanked and formally acknowledged fuels the next shift — and inspires others to join.",
      "Social Pulse tracks your verified volunteer hours, issues digital badges as you hit milestones, and highlights standout contributors in monthly community features. Your service becomes part of a public record of good.",
    ],
    sectionTitle: "How We Recognise Volunteers",
    sections: [
      { icon: "🏅", title: "Digital Badges and Milestones", body: "Automatically earn badges at 10, 50, 100 and 500 verified hours." },
      { icon: "📈", title: "Public Impact Profile", body: "Your Social Pulse profile shows hours served, causes supported and skills grown." },
      { icon: "📰", title: "Community Features", body: "Standout volunteers are highlighted in local content, digests and newsletters." },
      { icon: "🎖️", title: "Verified Hour Logs", body: "Downloadable certificates you can share with employers, schools or scholarship panels." },
      { icon: "📱", title: "Shareable Wins", body: "Celebrate your milestones with one-tap social cards designed for LinkedIn, Instagram and more." },
    ],
    closing: "You give freely. We make sure it counts — and that the world sees the impact you are quietly creating.",
    testimonial: {
      quote: "The badge I earned at 100 hours became my LinkedIn banner. Recruiters started asking about my work at the food bank before they asked about my job. Recognition doesn't just feel good — it opens doors.",
      name: "Sarah Chen",
      role: "Verified volunteer · Seattle",
      avatar: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=200&auto=format&fit=crop&q=80",
    },
  },

  {
    slug: "volunteer-awards",
    title: "Volunteer Awards",
    tagline: "Monthly Community Hero + annual recognition programs.",
    headline: "From Community Hero to National Champion — Awards That Honour Service",
    color: "#0d9488",
    emoji: "🏆",
    hero: "https://images.unsplash.com/photo-1531058020387-3be344556be6?w=1600&auto=format&fit=crop&q=80",
    intro: [
      "The most sustained, generous volunteers deserve more than a thank-you email. Social Pulse runs a tiered awards program — from monthly Community Hero shout-outs to annual awards for exceptional national and global contribution.",
      "Award nominations are open to everyone. A neighbour, a coordinator or a family member can nominate someone whose service has changed lives. Winners are celebrated publicly, and their stories become inspiration for others.",
    ],
    sectionTitle: "Our Awards Ladder",
    sections: [
      { icon: "🌟", title: "Community Hero of the Month", body: "One local volunteer per city, chosen from community nominations." },
      { icon: "🏵️", title: "Cause Champion of the Quarter", body: "Recognises outstanding service to a specific cause — food security, environment, seniors, and more." },
      { icon: "🏆", title: "Annual Social Pulse Award", body: "The signature national recognition — inspiring stories, published widely." },
      { icon: "🌍", title: "Global Ambassador Award", body: "For volunteers whose work has crossed borders and changed thousands of lives." },
      { icon: "🕯️", title: "Lifetime Service Honour", body: "Reserved for volunteers who have served their community for 20+ years." },
    ],
    closing: "Every award tells a story. Nominate the people making change happen — or keep showing up until it is your turn.",
    testimonial: {
      quote: "When my mom won Community Hero of the Month, our whole family cried. She'd cooked meals for hospice patients for eleven years without ever telling anyone. That award finally let us celebrate her out loud.",
      name: "Miguel Alvarez",
      role: "Family of a Community Hero · Manila",
      avatar: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=200&auto=format&fit=crop&q=80",
    },
  },

  {
    slug: "personal-growth",
    title: "Personal Growth",
    tagline: "Discover purpose, resilience and confidence.",
    headline: "Volunteering Grows the Person Inside the Volunteer",
    color: "#7c3aed",
    emoji: "🌱",
    hero: "https://images.unsplash.com/photo-1522204523234-8729aa6e3d5f?w=1600&auto=format&fit=crop&q=80",
    intro: [
      "There is a version of you that only shows up when someone else needs help. Braver. Kinder. More patient. Volunteering coaxes that person into the daylight, week after week, until they become who you are.",
      "The transformation is quiet. You notice you interrupt less, listen more, panic less easily, and take pride in small, specific things. That is personal growth — not from a self-help book, but from doing meaningful work with real people.",
    ],
    sectionTitle: "How Volunteering Grows You",
    sections: [
      { icon: "🧘", title: "Builds Patience and Empathy", body: "Working with people whose lives are unlike yours widens what you understand as normal." },
      { icon: "🛡️", title: "Strengthens Resilience", body: "You learn to keep going when a project falls apart, a shift is hard, or a plan changes." },
      { icon: "🌟", title: "Increases Confidence", body: "Every time you handle a situation you didn't think you could, your inner voice gets kinder." },
      { icon: "🎯", title: "Reveals Your Purpose", body: "Volunteering surfaces the causes that quietly matter most to you — and gives them a shape." },
      { icon: "🕰️", title: "Improves How You Use Time", body: "When two hours can change a life, you stop giving your time away to what doesn't matter." },
    ],
    closing: "The community changes when you volunteer. So do you. Both stories are worth telling.",
    testimonial: {
      quote: "I started volunteering to fix my anxiety, but what actually changed was my confidence. Something about handling real problems for real people rewires you. I'm still an introvert — I'm just no longer afraid.",
      name: "Emily Novak",
      role: "Youth mentor · Prague",
      avatar: "https://images.unsplash.com/photo-1544348817-5f2cf14b363f?w=200&auto=format&fit=crop&q=80",
    },
  },

  {
    slug: "improve-wellbeing",
    title: "Improve Wellbeing",
    tagline: "Volunteering is scientifically linked to better mental health.",
    headline: "Volunteering Helps Build a Healthier, Happier Life",
    color: "#16a34a",
    emoji: "😊",
    hero: "https://images.unsplash.com/photo-1518791841217-8f162f1e1131?w=1600&auto=format&fit=crop&q=80",
    intro: [
      "Helping others does more than strengthen communities — it can also improve your own physical, mental, and emotional wellbeing. Research has shown that volunteering is associated with reduced stress, increased happiness, stronger social connections, and a greater sense of purpose.",
      "When you volunteer, you become part of something larger than yourself. Whether you are helping a neighbor, supporting a local organization, mentoring young people, protecting the environment, or assisting those in need, every act of service creates positive experiences that benefit both the giver and the receiver.",
    ],
    sectionTitle: "Benefits of Volunteering for Wellbeing",
    sections: [
      { icon: "🧠", title: "Supports Mental Health", body: "Volunteering provides a sense of accomplishment and purpose, helping reduce feelings of loneliness, anxiety, and isolation. Meaningful activities and positive social interactions can improve overall emotional health." },
      { icon: "❤️", title: "Creates a Sense of Purpose", body: "Knowing that your time and skills make a difference can bring fulfillment and motivation. Volunteering helps people feel valued and connected to their communities." },
      { icon: "🤝", title: "Builds Social Connections", body: "Volunteering introduces you to new people, friendships, mentors, and community networks. Strong social relationships are an important part of long-term happiness and resilience." },
      { icon: "🌱", title: "Encourages Personal Growth", body: "Trying new activities, learning new skills, and taking on responsibilities can increase confidence and self-esteem." },
      { icon: "🚶", title: "Promotes an Active Lifestyle", body: "Many volunteer opportunities involve movement, teamwork, and outdoor activities, supporting better physical health and a more active daily routine." },
    ],
    closing: "You do not need to volunteer many hours to make an impact. A few hours spent helping others each month can create meaningful connections, improve your outlook, and contribute to a kinder, stronger community. When you give your time, you also receive the benefits of purpose, connection, and wellbeing.",
    testimonial: {
      quote: "The month I started volunteering at the community garden, my sleep improved and my Sunday-night dread disappeared. My therapist called it 'the cheapest antidepressant she'd ever prescribed.' I still show up every week.",
      name: "James Whitaker",
      role: "Community gardener · Melbourne",
      avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&auto=format&fit=crop&q=80",
    },
  },
];

// Fast lookup by slug for the detail page
export const VOLUNTEER_BENEFITS_BY_SLUG = Object.fromEntries(
  VOLUNTEER_BENEFITS.map((b) => [b.slug, b])
);
